// pano-crop.ts — on-demand loader + mount for the frame-in-viewer 360° poster/
// orientation chooser (#pano-crop-mount, cms_pano_fields.templ). Injects the
// standalone pano-editor IIFE (static/js/pano-editor/, built by
// `make build-pano-editor`) exactly once per page load, then mounts
// window.StudioPanoEditor into the caller's host element.
//
// FIXED (non-manifest-hashed) path: pano-editor.js is a small, rarely-changing
// standalone bundle loaded lazily (only when the operator actually opens the
// chooser), same rationale as static/js/json-editor/'s dist reference in
// templates/pages/admin_templates.templ — cache-busting via a content hash
// isn't worth threading the asset-manifest lookup through for a bundle that's
// never in the initial page payload anyway.
//
// MODULE-LEVEL (not Alpine-component) state: the picker's pano takeover pane
// (editingPanoFor) is mutually exclusive with every other pane and only ever
// shows ONE image at a time, so a single live viewer handle is sufficient —
// no need to key it by image id. image-picker.ts is the only caller.
const PANO_EDITOR_SRC = "/static/js/pano-editor/dist/pano-editor.js";

interface PanoEditorPose {
  yaw: number;
  pitch: number;
}

interface PanoEditorMarker {
  id: string;
  yaw: number;
  pitch: number;
  label?: string;
}

interface PanoEditorHandle {
  getPose(): PanoEditorPose;
  resize(): void;
  destroy(): void;
  setMarkers(markers: PanoEditorMarker[]): void;
}

// Window.StudioPanoEditor is ONE global (the pano-editor.js bundle mounts
// itself once, shared by this crop chooser AND tour-link-editor.ts's
// two-pane placement UI). Its ambient shape here must stay structurally
// identical to tour-link-editor.ts's copy of this same declaration — TS
// merges same-named global interface members across files and errors
// (TS2717/TS2741) on any mismatch. This file only ever calls
// getPose/resize/destroy (never setMarkers or placementMode), but the
// ambient type still has to describe the full real API surface.
declare global {
  interface Window {
    StudioPanoEditor?: {
      mount(
        container: HTMLElement,
        equirectUrl: string,
        opts?: {
          yaw?: number;
          pitch?: number;
          placementMode?: boolean;
          markers?: PanoEditorMarker[];
          onPlace?(pose: PanoEditorPose): void;
        },
      ): PanoEditorHandle;
    };
  }
}

// Single-flight script load, guarded by a module-level Promise: concurrent/
// repeated openPanoChooser() calls (open, close, reopen) must not inject the
// <script> tag more than once. Cleared on failure so a later retry (e.g. a
// transient network blip) gets a fresh attempt instead of replaying a
// rejected promise forever.
let loadPromise: Promise<void> | null = null;
let currentHandle: PanoEditorHandle | null = null;
// Bumped by every teardown (and every open). openPanoChooser awaits the script
// load + mount; if a teardown or a newer open happens DURING those awaits, the
// stale continuation must not mount an orphaned WebGL viewer into a dismissed
// host. It checks its captured token after the awaits and bails if superseded.
let openToken = 0;

function loadPanoEditor(): Promise<void> {
  if (window.StudioPanoEditor) return Promise.resolve();
  if (loadPromise) return loadPromise;
  loadPromise = new Promise<void>((resolve, reject) => {
    const el = document.createElement("script");
    el.src = PANO_EDITOR_SRC;
    el.onload = () => resolve();
    el.onerror = () => {
      loadPromise = null;
      reject(new Error(`failed to load ${PANO_EDITOR_SRC}`));
    };
    document.head.appendChild(el);
  });
  return loadPromise;
}

// openPanoChooser loads the viewer bundle (if not already loaded) and mounts
// it into hostEl, framed at the given yaw/pitch (both undefined -> the
// bundle's own default, forward-facing). Tears down any existing handle
// first — defensive: the picker always tears down before reopening, but a
// stale handle must never leak a second WebGL context.
export async function openPanoChooser(
  hostEl: HTMLElement,
  equirectUrl: string,
  pose: { yaw?: number; pitch?: number },
): Promise<void> {
  teardownPanoChooser();
  const token = ++openToken;
  await loadPanoEditor();
  // Torn down or superseded by a newer open while the script/mount awaited —
  // don't mount an orphaned viewer into a host the operator already dismissed.
  if (token !== openToken) return;
  if (!window.StudioPanoEditor) {
    throw new Error("StudioPanoEditor missing after script load");
  }
  currentHandle = window.StudioPanoEditor.mount(hostEl, equirectUrl, pose);
}

// resizePanoChooser re-measures the host and resizes the WebGL canvas. Call
// this right after the host is revealed — the container may have been
// mounted (or laid out) while hidden/zero-size, e.g. behind an x-show whose
// transition hasn't settled yet.
export function resizePanoChooser(): void {
  currentHandle?.resize();
}

// getChosenPose reads the viewer's current framing in degrees. Returns null
// if no viewer is mounted (defensive — the "use this view" button is
// disabled until mount succeeds, so callers should not normally hit this).
export function getChosenPose(): PanoEditorPose | null {
  return currentHandle ? currentHandle.getPose() : null;
}

// teardownPanoChooser releases the WebGL context. Must be called on every
// path that dismisses the chooser pane — the "← Back" button, the dialog's
// native close (ESC/backdrop bypasses Back), AND a fresh modal open (belt-
// and-suspenders, mirrors _teardownSortable's destroy-first guard) — so a
// dismissed pane never leaks a live WebGL context. Safe to call when nothing
// is mounted.
export function teardownPanoChooser(): void {
  openToken++; // invalidate any in-flight openPanoChooser awaiting load/mount
  currentHandle?.destroy();
  currentHandle = null;
}
